/**
 * Real 360° product turnaround.
 * Two faces of a 3D flip card:
 *   - `imageUrl` = front image
 *   - `backImageUrl` (optional) = explicit back image (preferred when available)
 *   - If no backImageUrl + `splitFront` = true, treats imageUrl as a wide front+back
 *     composite and uses background-position to show the right half on the back face.
 *   - If no backImageUrl + `splitFront` = false (single-view), the back face mirrors
 *     the front (rotateY+scaleX) so the rotation still reads as a clean 3D turn.
 */
export default function Product360({
    imageUrl,
    backImageUrl = null,
    auto = false,
    splitFront = true,
    duration = 14,
    alt = "",
    className = "",
}) {
    const baseImg = {
        backgroundImage: `url(${imageUrl})`,
        backgroundRepeat: "no-repeat",
    };

    let frontStyle, backStyle;

    if (backImageUrl) {
        // Two separate clean images — each face shows its OWN image fully
        frontStyle = {
            ...baseImg,
            backgroundSize: "contain",
            backgroundPosition: "center",
        };
        backStyle = {
            backgroundImage: `url(${backImageUrl})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
        };
    } else if (splitFront) {
        // Split a wide front+back composite. `200% auto` preserves aspect ratio.
        frontStyle = {
            ...baseImg,
            backgroundSize: "200% auto",
            backgroundPosition: "left center",
        };
        backStyle = {
            ...baseImg,
            backgroundSize: "200% auto",
            backgroundPosition: "right center",
        };
    } else {
        // Single view — back face shows the same image flipped
        frontStyle = {
            ...baseImg,
            backgroundSize: "contain",
            backgroundPosition: "center",
        };
        backStyle = {
            ...baseImg,
            backgroundSize: "contain",
            backgroundPosition: "center",
            transform: "rotateY(180deg) scaleX(-1)",
        };
    }

    return (
        <div
            className={`flip3d-container ${auto ? "auto" : ""} ${className}`}
            style={auto ? { "--flip3d-duration": `${duration}s` } : undefined}
            role="img"
            aria-label={alt}
        >
            <div className="flip3d-inner">
                <div className="flip3d-face flip3d-front" style={frontStyle} aria-hidden="true" />
                <div className="flip3d-face flip3d-back" style={backStyle} aria-hidden="true" />
            </div>
            {auto && (
                <span className="absolute bottom-3 left-3 z-20 font-mono text-[9px] uppercase tracking-[0.3em] bg-black/70 px-2 py-1 backdrop-blur-sm pointer-events-none">
                    360°
                </span>
            )}
        </div>
    );
}
